import { Avatar, AvatarGroup, IconButton, Stack, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Pencil, Trash } from "@phosphor-icons/react";
import { DeleteModal } from "components/UI";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import { useDenominationStore } from "features/denominations";
import queryString from "query-string";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useCentralStore } from "stores";
import { useDeleteRole, useRolesPaginated } from "../api";

export const UsersRoleTable = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );
  const [role, setRole] = useState(null);
  const deleteMutation = useDeleteRole();
  const openDeleteModal = useCentralStore(state => state.openDeleteModal);

  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const [page, setPage] = useState(Number(queryParams.page) || 1);
  const [pageSize, setPageSize] = useState(Number(queryParams.pageSize) || 10);

  useEffect(() => {
    const query = queryString.stringify({ page, pageSize });
    navigate(`?t=roles&${query}`, { replace: true });
  }, [page, pageSize, navigate]);

  const rolesPaginatedQuery = useRolesPaginated({
    churchId: selectedChurch?.id,
    page: page,
    pageSize: pageSize,
    isDenomination: window.location.pathname.includes("denomination"),
    denominationId: selectedDenomination?.id
  });

  const columns = [
    {
      field: "name",
      headerName: t("systemAdmins.roles.RoleName"),
      flex: 1
    },
    {
      field: "users",
      headerName: t("systemAdmins.roles.Users"),
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <AvatarGroup max={4}>
            {row?.users?.map(user => (
              <Tooltip
                key={user?.id}
                title={`${user?.first_name} ${user?.last_name}`}
              >
                <Avatar
                  sx={{ border: "2px solid", borderColor: "primary.main" }}
                  src={`${IMAGE_URL}/${user?.image_url}`}
                  alt={user?.first_name}
                />
              </Tooltip>
            ))}
          </AvatarGroup>
        );
      }
    },
    {
      field: "description",
      headerName: t("systemAdmins.roles.RoleDescription"),
      flex: 2
    },
    {
      field: "operation",
      headerName: t("systemAdmins.roles.Operations"),
      sortable: false,
      renderCell: ({ row }) => {
        const publicRoles = ["OWNER", "ADMIN", "VIEWER", "ACCOUNTANT"];

        if (publicRoles.includes(row.name)) {
          return null;
        }

        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              onClick={() => {
                navigate(`/roles/${row.id}`);
              }}
            >
              <Pencil />
            </IconButton>
            <IconButton
              onClick={() => {
                setRole(row);
                openDeleteModal();
              }}
            >
              <Trash />
            </IconButton>
          </Stack>
        );
      },
      flex: 1
    }
  ];

  return (
    <>
      <DeleteModal
        name={role?.name}
        onDelete={() => {
          deleteMutation.mutate({
            churchId: selectedChurch?.id,
            roleId: role?.id,
            isDenomination: window.location.pathname.includes("denomination"),
            denominationId: selectedDenomination?.id
          });
        }}
      />
      <DataGrid
        autoHeight
        rows={rolesPaginatedQuery?.data?.result?.data || []}
        rowCount={rolesPaginatedQuery?.data?.result?.total || 0}
        loading={rolesPaginatedQuery?.isLoading}
        columns={columns}
        pagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
        disableSelectionOnClick
        disableColumnMenu
        density="comfortable"
        getRowId={row => row.id}
        paginationMode="server"
        page={page - 1}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage + 1)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        localeText={{
          noRowsLabel: t("teams.teamProfile.NoRows"),
          MuiTablePagination: {
            labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
          }
        }}
      />
    </>
  );
};
